<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { WidgetCreditsListMeiliSearch } from '~/components/widget/credits'
import { WidgetSelectCreditType } from '~/components/widget/select-credit-type'
import { WidgetSelectCreditPurpose } from '~/components/widget/select-credit-purpose'
import { WidgetCreditTypePills } from '~/components/widget/credit-type-pills'
import type { IResponseData } from '~/types'
import type { ICreditTypeData, ICreditPurposeData } from '~/components/widget/credits'
import { WidgetSeoContent, WidgetSeoHeroBanner } from '~/components/widget/seo'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { heroBanner } from '~/data/pages/credits/mocks'
import { WidgetFaqList } from '~/components/widget/faq-list'
import type { ICreditsQueryParams } from '~/components/entity/credit/item'
import { WidgetBestOffer } from '~/components/widget/best-offer'
import { WidgetDiscussionsAskSpecialist } from '~/components/widget/discussions'
import { WidgetScrollTo } from '~/components/widget/scroll-to'
import { ANCHOR_IDS } from '~/lib/constants'
import {
  WidgetMonitorCreditRates,
  WidgetMonitorNbkrDynamics
} from '~/components/widget/monitor'

const { t, locale } = useI18n()
const { pathWithoutLocale } = useLocalizedPath()

definePageMeta({
  path: '/credits',
  alias: ['/credits/purpose/:purpose(.*)*', '/credits/:type(.*)*']
})

const route = useRoute()

const pageInfo = reactive({
  title: t('page.credits.default_title')
})

const creditsListQueryParams = reactive<ICreditsQueryParams>({
  per_page: '20'
})

const typeParam = route.params.purpose ? 'purpose' : route.params.type ? 'type' : null

const paramValue = typeParam ? route.params[typeParam] : null

if (paramValue && typeParam) {
  const url =
    typeParam === 'purpose' ? `/credit_purposes/${paramValue}` : `/credit_types/${paramValue}`

  const key = `credits-${typeParam}-${paramValue}`

  const { data } = await useApi<IResponseData<ICreditPurposeData | ICreditTypeData>>(url, {
    key
  })

  if (data.value) {
    pageInfo.title = data.value.data.title
  } else {
    showError({ statusCode: 404 })
  }
}

// The market monitor is keyed by credit type only; purpose pages have no matching endpoint.
const monitorCreditType = computed(() => {
  if (typeParam !== 'type' || !paramValue) return null

  return Array.isArray(paramValue) ? paramValue[0] : paramValue
})

const breadcrumbs = computed(() => {
  const links = []

  if (paramValue && paramValue[0] === 'islamic') {
    links.push({ title: pageInfo.title })
  } else {
    links.push({ title: t('page.credits.breadcrumbs_title'), url: '/credits' })
    if (paramValue && pageInfo.title) {
      links.push({ title: pageInfo.title, url: route.path })
    }
  }

  return links
})

const isIslamic = computed(() => route.params.type && route.params.type[0] === 'islamic')
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />

      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <!-- Below `xl` the type menu is rendered above the credits list instead -->
            <WidgetSelectCreditType
              :query-params="{ 'filter[position]': 'sidebar' }"
              class="hidden xl:block"
            />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
            <WidgetSelectCreditPurpose :query-params="{ 'filter[position]': 'sidebar' }" />
            <WidgetScrollTo
              v-if="monitorCreditType"
              :title="t('monitor.sidebar_scroll_to.title')"
              :description="t('monitor.sidebar_scroll_to.description')"
              :button-text="t('monitor.sidebar_scroll_to.button_text')"
              :target="ANCHOR_IDS.WIDGET_MARKET_MONITOR"
            />
            <WidgetDiscussionsAskSpecialist />
          </aside>
        </template>

        <div class="space-y-5" role="main">
          <WidgetSeoContent
            v-if="typeParam && !isIslamic"
            position="upper"
            :title="pageInfo.title"
          />

          <WidgetSeoHeroBanner
            v-else
            :heading="heroBanner.heading"
            :text="heroBanner.text"
            :image="isIslamic ? heroBanner.image_islamic : heroBanner.image"
          />

          <WidgetReviveAd zoneid="39" class="overflow-hidden rounded-lg" />

          <WidgetCreditTypePills />
          <WidgetCreditsListMeiliSearch :query-params="creditsListQueryParams" />
          <WidgetBestOffer
            v-if="route.fullPath === '/credits' || route.fullPath === `/${locale}/credits`"
          />

          <div
            v-if="monitorCreditType"
            :id="ANCHOR_IDS.WIDGET_MARKET_MONITOR"
            class="space-y-5"
          >
            <WidgetMonitorCreditRates :type="monitorCreditType" />
            <WidgetMonitorNbkrDynamics product="credit" :category="monitorCreditType" />
          </div>

          <WidgetSeoContent position="bottom" />

          <WidgetFaqList
            :query-params="{
              'filter[faqable_url]': pathWithoutLocale
            }"
          />
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
